import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/config/routes.config';
import { SKIP_AUTH } from '@/config/skipAuth';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Required permissions. By default ANY of them grants access; requireAll flips to ALL. */
  permissions?: string[];
  /** Required roles (ANY grants access, unless requireAll). */
  roles?: string[];
  requireAll?: boolean;
}

/**
 * Real auth gate. Renders children only for an authenticated session that also
 * satisfies the (optional) permission/role requirements. An unauthenticated user
 * is redirected to /login (preserving where they were headed); a logged-in user
 * lacking the rights is sent to the app home rather than shown the page.
 */
function ProtectedRoute({ children, permissions, roles, requireAll = false }: ProtectedRouteProps) {
  const { isAuthenticated, accessToken, loading, user } = useAuth();
  const location = useLocation();

  if (SKIP_AUTH) {
    return <>{children}</>;
  }

  // While the session is being (re)hydrated, don't flash the login screen.
  if (loading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', color: '#8b8b8b' }}>
        Loading…
      </div>
    );
  }

  // Hard gate: no valid session → login.
  if (!isAuthenticated || !accessToken) {
    return <Navigate to={ROUTES.LOGIN} replace state={{ from: location }} />;
  }

  // Granular permission/role checks are enforced ONLY when the backend actually
  // populates them on the user — so an authenticated account is never locked out
  // of the app just because the token omits a permissions/roles array.
  const userPerms = Array.isArray(user?.permissions) ? user!.permissions : [];
  const userRole = user?.role;

  const permOk =
    !permissions?.length || userPerms.length === 0
      ? true
      : requireAll
        ? permissions.every((p) => userPerms.includes(p))
        : permissions.some((p) => userPerms.includes(p));

  const roleOk = !roles?.length || !userRole ? true : roles.includes(userRole);

  if (!permOk || !roleOk) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
